import React, { Fragment, useEffect, useState } from "react";
import "./SupplierDashboard.scss";
import { DatePicker, Tooltip } from "antd";
import SortableTree from "react-sortable-tree";
import "react-sortable-tree/style.css";
import {
  fetchSupplierAlocations,
  fetchSuppliersWithTotalEmissionRecords,
} from "../../../services/srmService";
import { getSettingPreferences } from "../../../services/orgService";
import dayjs from "dayjs";
import { Moment } from "moment";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { t } from "i18next";
import { toastMessage } from "../../../helper";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SupplierAllocationsDialog from "./SupplierAllocationsDialog";
import { dateParserWithoutTimeAndDate } from "../../../helper";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";

function SupplierEmissionSources() {
  const { RangePicker } = DatePicker;
  type RangeValue = [Moment | null, Moment | null] | null;
  const monthFormat = "YYYY/MM";
  let cellColumns = ["Name", "Total Allocated Emissions(tCO2e)", " ", " "];

  const categoryMapping: any = {
    "Listed equity and corporate bonds": "A",
    "Business loans and unlisted equity": "B",
    "Project finance": "C",
    "Commercial real estate": "D",
    Mortgages: "F",
    "Motor vehicle loans": "E",
    "Sovereign debt": "H",
  };

  const [controller, setController] = useState<any>({
    page: 0,
    limit: 10,
    sortBy: null,
    sortOrder: null,
    filters: {},
    columns: {
      Name: {
        type: "string",
        hasSortBy: true,
        filterOptions: {},
      },
      "Total Allocated Emissions(tCO2e)": {
        type: "string",
        hasSortBy: true,
        filterOptions: {},
      },
    },
  });
  const [defaultDateRange, setDefaultRange] = useState<any>([]);
  const [showDateRange, setShowDateRange] = useState(false);
  const [dates, setDates] = useState<RangeValue>(null);
  const [startMonth, setStartMonth] = useState(
    new Date(new Date().getFullYear(), 0, 2)
  );
  const [endMonth, setEndMonth] = useState(
    new Date(new Date().getFullYear(), 11, 31)
  );
  const [treeData, setTreeData] = useState([] as any[]);
  const [tableData, setTableData] = useState([] as any[]);
  const [showSupplierChart, setShowSupplierChart] = useState(false);
  const [totalEmissions, setTotalEmissions] = useState(0);
  const [openAllocationsDialog, setOpenAllocationsDialog] = useState(false);
  const [allocationsList, setAllocationsList] = useState([] as any[]);
  const [searchText, setSearchText] = useState("");

  const handleAllocationDialogClose = () => {
    setOpenAllocationsDialog(false);
  };

  const onChangeDate = (e: any) => {
    let startDate = new Date(e[0].$d);
    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 2);
    setStartMonth(startDate);
    let endDate = new Date(e[1].$d);
    endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1);
    setEndMonth(endDate);
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
  };

  const setSupplierSortOptions = (columnName: any) => {
    if (columnName in controller.columns) {
      let selectedColumnData = controller.columns[columnName];
      if (
        selectedColumnData.hasSortBy &&
        Object.keys(selectedColumnData?.filterOptions)?.length === 0
      ) {
        const filterController: any = {
          sortBy: columnName,
        };
        if (controller.sortBy === columnName) {
          filterController["sortOrder"] =
            controller.sortOrder === "ASC" ? "DESC" : "ASC";
        } else {
          filterController["sortOrder"] = "ASC";
        }
        controller.sortBy = filterController.sortBy;
        controller.sortOrder = filterController.sortOrder;
        setController({ ...controller });
      }
    }
  };

  const fetchSuppliers = (supplier: any, isTree: boolean) => {
    if (supplier.company) {
      fetchSuppliersWithTotalEmissionRecords(
        startMonth,
        endMonth,
        supplier.company.toString(),
        "",
        null
      ).then((res) => {
        if (res?.data?.length) {
          if (isTree) {
            supplier.children = res.data.map((eachSupplier: any) => {
              return {
                ...eachSupplier,
                id: eachSupplier.id.toString(),
                title: eachSupplier.name,
                expanded: true,
                children: [],
              };
            });
            setTreeData([...treeData]);
          } else {
            supplier.children = res.data.map((eachSupplier: any) => {
              return {
                ...eachSupplier,
                id: eachSupplier.id.toString(),
                name: eachSupplier.name,
                children: [],
              };
            });
            setTableData([...tableData]);
          }
        }
      });
    }
  };

  const getAllocationName = (
    approachMethod: string,
    allocationApproach: string
  ) => {
    switch (approachMethod) {
      case "allocation":
        return allocationApproach === "production volume"
          ? "Production volume approach"
          : allocationApproach === "fixed percentage"
          ? "Allocate by fixed percentage"
          : "Economic";
      case "product":
        return "Product carbon footprint";
      case "investor":
        return allocationApproach === "outstanding value based"
          ? "Outstanding value based allocation"
          : allocationApproach === "fixed attribution factor"
          ? "Fixed attribution factor"
          : "-";
      case "none":
        return "None";
      default:
        return "-";
    }
  };

  const fetchAllocations = (supplier: any, isTree: boolean) => {
    const customerID = supplier.customer;
    if (supplier.company && supplier.allocated_total_emissions && customerID) {
      fetchSupplierAlocations(
        startMonth,
        endMonth,
        customerID.toString(),
        supplier.company.toString()
      ).then((res: any) => {
        if (res?.data?.allocations?.length) {
          const allocations = res.data.allocations.map(
            (eachAllocation: any) => {
              return {
                ...eachAllocation,
                name: eachAllocation.approach_method
                  ? getAllocationName(
                      eachAllocation.approach_method,
                      eachAllocation.allocation_approach
                    )
                  : getAllocationName(
                      "investor",
                      eachAllocation.allocation_method
                    ),
              };
            }
          );
          //setAllocationsList(allocations);
          if (isTree) {
            setAllocationsList(allocations);
            setOpenAllocationsDialog(true);
          } else {
            setAllocationsList((prev) => ({
              ...prev,
              [supplier.id]: allocations,
            }));
          }
        } else {
          toastMessage(
            "No allocation records found in selected period.",
            "info"
          );
        }
      });
    } else {
      toastMessage("No allocation records found in selected period.", "info");
    }
  };

  useEffect(() => {
    getSettingPreferences(true).then((res) => {
      const companyData = res.data.companyPreference;
      if (companyData.is_calendar_year) {
        setDefaultRange([
          dayjs(new Date().getFullYear() + "/01/01", monthFormat),
          dayjs(new Date().getFullYear() + "/12/31", monthFormat),
        ]);
      } else {
        if (companyData.financial_year) {
          const dateRange = companyData.financial_year.split("to");
          let startMonth =
            (dateRange[0].trim().length === 1 ? "0" : "") + dateRange[0].trim();
          let endMonth =
            (dateRange[1].trim().length === 1 ? "0" : "") + dateRange[1].trim();
          setDefaultRange([
            dayjs(
              new Date().getFullYear() + "/" + startMonth + "/01",
              monthFormat
            ),
            dayjs(
              new Date().getFullYear() +
                1 +
                "/" +
                endMonth +
                "/" +
                new Date(
                  new Date().getFullYear() + 1,
                  Number(endMonth) - 1,
                  0
                ).getDate(),
              monthFormat
            ),
          ]);

          setStartMonth(
            new Date(new Date().getFullYear(), Number(startMonth) - 1, 2)
          );
          setEndMonth(
            new Date(
              new Date().getFullYear() + 1,
              Number(endMonth) - 1,
              new Date(
                new Date().getFullYear() + 1,
                Number(endMonth) - 1,
                0
              ).getDate()
            )
          );
        }
      }
      setShowDateRange(true);
    });
  }, []);

  useEffect(() => {
    const company = JSON.parse(localStorage.getItem("company") || "{}");
    if (startMonth && endMonth) {
      fetchSuppliersWithTotalEmissionRecords(
        startMonth,
        endMonth,
        company.id,
        searchText,
        controller
      ).then((res) => {
        if (res?.data?.length) {
          const updatedData = res.data.map((eachSupplier: any) => ({
            ...eachSupplier,
            children: eachSupplier.children || [],
          }));
          setTableData(updatedData);
          let totalEmissionsValue = 0;
          res.data.forEach((eachSupplier: any) => {
            totalEmissionsValue += eachSupplier.allocated_total_emissions || 0;
          });
          setTotalEmissions(Number(totalEmissionsValue.toFixed(2)));
          const suppliersList = [];
          suppliersList.push({
            id: company?.id,
            name: "treeHead",
            title: "treeHead",
            expanded: true,
            children: res.data.map((eachSupplier: any) => {
              return {
                ...eachSupplier,
                id: eachSupplier.id.toString(),
                title: eachSupplier.name,
                expanded: true,
                children: [],
              };
            }),
          });
          setTreeData(suppliersList);
          // setShowSupplierChart(true);
        } else {
          setTotalEmissions(0);
          setTableData([]);
          setTreeData([
            {
              id: company?.id,
              name: "treeHead",
              title: "treeHead",
              expanded: true,
            },
          ]);
        }
      });
    }
  }, [startMonth, endMonth, searchText, controller]);

  const [expandedRows, setExpandedRows] = useState<any>([]);
  const handleFieldValue = (value: any) => {
    if (value === "" || value === null) {
      return "N.A";
    }
    return value;
  };

  const handleRowClick = (supplier: any) => {
    setExpandedRows((prev: { [x: string]: any }) => ({
      ...prev,
      [supplier.id]: !prev[supplier.id],
    }));
    if (!expandedRows[supplier.id]) {
      fetchAllocations(supplier, false);
    }
  };

  const renderAllocationInfo = (allocationsList: any[]) => {
    return (
      <>
        <div className="w-full h-full flex flex-col justify-start items-start divide-y-[1px] bg-white">
          {allocationsList.length
            ? allocationsList.map((eachAllocation: any) => (
                <div className="w-full flex flex-col p-4 pr-8 ">
                  <div className="w-full flex flex-row text-sm justify-between pb-2">
                    <div>
                      {dateParserWithoutTimeAndDate(eachAllocation.from_date)}
                      {" - "}
                      {dateParserWithoutTimeAndDate(eachAllocation.to_date)}
                    </div>
                    <div style={{ paddingRight: "4.5rem" }}>
                      {handleFieldValue(
                        eachAllocation.allocated_total_emissions
                      )}
                      <span className="text-xs"></span>
                    </div>
                  </div>
                  <div className="w-full flex flex-row justify-start items-center text-xs mt-2 mb-1 pl-4">
                    <span className="pw-neutral-600-color ">
                      Allocated Category:
                    </span>
                    &nbsp;
                    <span>
                      {eachAllocation.category_name
                        ? `C5.4${
                            categoryMapping[eachAllocation.category_name] +
                            " " +
                            eachAllocation.category_name
                          }`
                        : "C4.1 Purchase Goods & Services"}
                    </span>
                  </div>
                  <div
                    className="w-full flex flex-row justify-start items-center text-xs mt-2 mb-1 pl-4"
                    onClick={() => {
                      console.log(allocationsList);
                    }}
                  >
                    <span className="pw-neutral-600-color ">
                      Allocation method:
                    </span>
                    &nbsp;
                    <span>{eachAllocation.name}</span>
                  </div>
                  {eachAllocation.name === "Allocate by fixed percentage" ||
                  eachAllocation.name === "Fixed attribution factor" ? (
                    <div className="w-full flex flex-row justify-start items-center text-xs my-1 pl-4">
                      <span className="pw-neutral-600-color">
                        Allocation percentage:{" "}
                      </span>
                      &nbsp;
                      {handleFieldValue(eachAllocation.allocated_percentage)}
                    </div>
                  ) : (
                    ""
                  )}
                  {eachAllocation.name === "Production volume approach" ||
                  eachAllocation.name ==
                    "Outstanding value based allocation" ? (
                    <div className="w-full flex flex-row justify-start items-center text-xs my-1 pl-4">
                      <span className="pw-neutral-600-color">
                        Allocation percentage:{" "}
                      </span>
                      &nbsp;
                      {eachAllocation.total_production &&
                      eachAllocation.supplied_count &&
                      !isNaN(eachAllocation.total_production) &&
                      !isNaN(eachAllocation.supplied_count)
                        ? (parseFloat(eachAllocation.supplied_count) * 100) /
                          parseFloat(eachAllocation.total_production)
                        : eachAllocation.numerator &&
                          eachAllocation.denominator &&
                          !isNaN(eachAllocation.numerator) &&
                          !isNaN(eachAllocation.denominator)
                        ? (parseFloat(eachAllocation.numerator) * 100) /
                          parseFloat(eachAllocation.denominator)
                        : "-"}
                    </div>
                  ) : (
                    ""
                  )}
                  {eachAllocation.name === "Economic" ? (
                    <div className="w-full flex flex-row justify-start items-center text-xs my-1 pl-4">
                      <span className="pw-neutral-600-color">
                        Allocation percentage:{" "}
                      </span>
                      &nbsp;
                      {eachAllocation.total_revenue &&
                      eachAllocation.sales &&
                      !isNaN(eachAllocation.total_revenue) &&
                      !isNaN(eachAllocation.sales)
                        ? (parseFloat(eachAllocation.sales) * 100) /
                          parseFloat(eachAllocation.total_revenue)
                        : "-"}
                    </div>
                  ) : (
                    ""
                  )}
                  {eachAllocation.name === "Product carbon footprint" ? (
                    eachAllocation.transactions?.length ? (
                      eachAllocation.transactions
                        .filter(
                          (eachTransaction: any) =>
                            eachTransaction.emission_factor
                        )
                        .map((eachTransaction: any) => (
                          <div className="w-full flex flex-row justify-between items-center text-xs my-1 pl-4">
                            <div>
                              <span className="pw-neutral-600-color">
                                Product:{" "}
                              </span>
                              {handleFieldValue(eachTransaction.field_name)}
                            </div>
                            <div>
                              <span className="pw-neutral-600-color">
                                Total number of units:{" "}
                              </span>
                              {handleFieldValue(eachTransaction.total_units)}
                            </div>
                            <div>
                              <span className="pw-neutral-600-color">
                                Emission per unit(tCO2e):{" "}
                              </span>
                              {handleFieldValue(
                                eachTransaction.emission_factor
                              )}
                            </div>
                            <div>
                              <span className="pw-neutral-600-color">
                                Emission(tCO2e):{" "}
                              </span>
                              {eachTransaction.value}
                            </div>
                          </div>
                        ))
                    ) : (
                      ""
                    )
                  ) : (
                    <div className="w-full flex flex-row justify-between items-center text-xs my-1 pl-4">
                      <div>
                        <span className="pw-neutral-600-color">
                          Scope 1(tCO2e):{" "}
                        </span>
                        {handleFieldValue(eachAllocation.allocated_scope1)}
                      </div>
                      <div>
                        <span className="pw-neutral-600-color">
                          Scope 2(tCO2e):{" "}
                        </span>
                        {handleFieldValue(eachAllocation.allocated_scope2)}
                      </div>
                      <div>
                        <span className="pw-neutral-600-color">
                          Scope 3(tCO2e):{" "}
                        </span>
                        {handleFieldValue(eachAllocation.allocated_scope3)}
                      </div>
                    </div>
                  )}
                </div>
              ))
            : ""}
          <div className="w-full h-full flex bg-neutral-100 p-1.5"></div>
        </div>
      </>
    );
  };
  const renderTable = (suppliers: any[], parentSupplier: any) => {
    return (
      <TableContainer className="pw-supplier-dashboard-table bg-neutral-100">
        <Table className="">
          <TableHead>
            <TableRow>
              {cellColumns.map((eachCell, index) => (
                <TableCell
                  key={index}
                  className={`bg-neutral-50 text-success text-m truncate !font-bold cursor-pointer`}
                  id={eachCell + "-th"}
                  style={{
                    border: "none",
                    width:
                      index === 0 ? "800px" : index === 1 ? "250px" : "50px",
                    gap: index === 0 ? "8px" : index === 1 ? "8px" : "0px",
                    color: "text-neutral-600",
                    fontSize: "16px",
                  }}
                  align={index === 1 ? "right" : "left"}
                  onClick={() => {
                    return tableData?.length && !parentSupplier
                      ? setSupplierSortOptions(eachCell)
                      : "";
                  }}
                >
                  {eachCell.trim()}
                  {eachCell !== " " &&
                    !parentSupplier &&
                    controller.sortBy === eachCell &&
                    (controller.sortOrder === "ASC" ? (
                      <Tooltip
                        title={
                          "Ascending Order - Click to sort in descending order"
                        }
                        placement="bottomLeft"
                        arrow={false}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title="Descending Order - Click to sort in ascending order"
                        placement="bottomLeft"
                        arrow={false}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      </Tooltip>
                    ))}
                </TableCell>
              ))}
            </TableRow>
            {!parentSupplier ? (
              <TableRow className="bg-total-row w-full">
                <TableCell className="pw-supplier-emissions-border">
                  Total Emissions(tCO2e)
                </TableCell>
                <TableCell
                  className="pw-supplier-emissions-border"
                  align="right"
                >
                  {parentSupplier
                    ? handleFieldValue(parentSupplier.allocated_total_emissions)
                    : handleFieldValue(totalEmissions)}
                </TableCell>
                <TableCell className="pw-supplier-emissions-border" />
                <TableCell className="pw-supplier-emissions-border" />
              </TableRow>
            ) : (
              ""
            )}
          </TableHead>
          <TableBody>{renderTableRows(suppliers)}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTableRows = (suppliers: any[]) => {
    return suppliers.flatMap((eachSupplier) => {
      const isExpanded = expandedRows[eachSupplier.id];
      return [
        <Fragment key={eachSupplier.id}>
          <TableRow className="w-full bg-white">
            <TableCell className="pw-supplier-emissions-border">
              {eachSupplier.name}
            </TableCell>
            <TableCell align="right" className="pw-supplier-emissions-border">
              {handleFieldValue(eachSupplier.allocated_total_emissions)}
            </TableCell>
            <TableCell className="pw-supplier-emissions-border">
              <IconButton
                size="small"
                className="pw-supplier-emissions-icon-button"
                onClick={() => {
                  if (
                    !(eachSupplier.children && eachSupplier.children.length)
                  ) {
                    fetchSuppliers(eachSupplier, false);
                  } else {
                    eachSupplier.children = [];
                    setTableData([...tableData]);
                  }
                }}
              >
                {eachSupplier.has_suppliers ? (
                  eachSupplier.children.length ? (
                    <ArrowDropUp />
                  ) : (
                    <ArrowDropDown />
                  )
                ) : (
                  ""
                )}
              </IconButton>
            </TableCell>
            <TableCell className="pw-supplier-emissions-border" />
          </TableRow>
          <TableRow className="bg-neutral-100 w-full pw-supplier-emissions-border">
            <TableCell
              className="pw-supplier-emissions-border"
              sx={{
                border: "none",
                paddingTop: 0.5,
                paddingBottom: 0.5,
                fontSize: "14px",
              }}
            >
              View allocation information
              <IconButton
                size="small"
                className="pw-supplier-emissions-icon-button"
                onClick={() => handleRowClick(eachSupplier)}
              >
                {isExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
              </IconButton>
            </TableCell>

            <TableCell className="pw-supplier-emissions-border" />
            <TableCell className="pw-supplier-emissions-border" />
            <TableCell className="pw-supplier-emissions-border" />
          </TableRow>
          <TableRow>
            <TableCell
              colSpan={4}
              style={{ paddingBottom: 0, paddingTop: 0, border: "none" }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {isExpanded && allocationsList[eachSupplier.id]
                  ? renderAllocationInfo(allocationsList[eachSupplier.id])
                  : ""}
              </Collapse>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              className="bg-neutral-100"
              style={{
                paddingBottom: 0,
                paddingTop: 0,
                borderBottom: "none",
              }}
              colSpan={4}
            >
              <Collapse in={eachSupplier.children.length > 0}>
                <Box margin={1}>
                  {renderTable(eachSupplier.children, eachSupplier)}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </Fragment>,
      ];
    });
  };

  return (
    <Fragment>
      <div className="w-full flex flex-col">
        <div className="w-full flex flex-row items-center justify-between">
          <p className="text-xl">
            <b>Emission sources</b>
          </p>
          <div className="flex flex-row items-center gap-6">
            <OutlinedInput
              id="outlined-adornment-password "
              className="pw-search-field"
              placeholder={t("Search")}
              onChange={handleSearch}
              endAdornment={
                <InputAdornment position="end">
                  {searchText === "" ? (
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  ) : (
                    <IconButton edge="end">
                      <CloseIcon
                        onClick={() => {
                          setSearchText("");
                        }}
                      />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
            <div className="flex flex-row items-center">
              <img
                className="cursor-pointer"
                src={
                  !showSupplierChart
                    ? "assets/icons/list-selected.svg"
                    : "assets/icons/list.svg"
                }
                onClick={() => {
                  setShowSupplierChart(false);
                }}
                alt=" "
              />
              <img
                className="cursor-pointer"
                src={
                  showSupplierChart
                    ? "assets/icons/tree-chart.svg"
                    : "assets/icons/tree-notSelected.svg"
                }
                onClick={() => {
                  setShowSupplierChart(true);
                }}
                alt=" "
              />
            </div>
            {showDateRange && (
              <RangePicker
                className="pw-selectbunit"
                picker="month"
                onCalendarChange={(val: any) => {
                  setDates(val);
                }}
                onChange={onChangeDate}
                onOpenChange={onOpenChange}
                placeholder={["From", "To"]}
                defaultValue={defaultDateRange}
                format={monthFormat}
                getPopupContainer={(triggerNode: any) => {
                  return triggerNode.parentNode;
                }}
              />
            )}
          </div>
        </div>
        {showSupplierChart ? (
          <div
            style={{ height: "auto" }}
            className="pw-bg-neutral-100-color mt-8 py-8 px-4"
          >
            <SortableTree
              treeData={treeData}
              onChange={(treeData) => setTreeData(treeData)}
              canDrag={({ node }) => false}
              isVirtualized={false}
              generateNodeProps={(rowInfo) => ({
                title: (
                  <div style={{ width: "100%" }} className="flex flex-col">
                    {rowInfo.node.name === "treeHead" ? (
                      <div>Total Emissions(tCO2e): {totalEmissions}</div>
                    ) : (
                      <>
                        <div className="text-sm font-normal">
                          {rowInfo.node.name}
                        </div>
                        {/* <div className="text-xs font-normal flex flex-row items-center justify-between pw-neutral-600-color">
                          <div>
                            Scope 1(tCO2e): {rowInfo.node.allocated_scope1 || 0}
                          </div>
                          <div className="my-1">
                            Scope 2(tCO2e): {rowInfo.node.allocated_scope2 || 0}
                          </div>
                          <div>
                            Scope 3(tCO2e): {rowInfo.node.allocated_scope3 || 0}
                          </div>
                        </div> */}
                        <div className="text-sm font-semibold flex flex-row items-center justify-between">
                          <div>
                            Total Emissions(tCO2e):{" "}
                            {handleFieldValue(
                              rowInfo.node.allocated_total_emissions
                            )}
                          </div>
                          <div>
                            {rowInfo.node.has_suppliers &&
                            !rowInfo.node.children?.length ? (
                              <img
                                className="cursor-pointer"
                                src="assets/icons/arrow-right-home-page.svg"
                                onClick={() => {
                                  fetchSuppliers(rowInfo.node, true);
                                }}
                                alt=" "
                              />
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                        <div className="text-xs font-normal flex flex-row items-center justify-start pw-view-allocation-border-top mt-1 pt-1 cursor-pointer">
                          <div
                            onClick={() => {
                              fetchAllocations(rowInfo.node, true);
                            }}
                          >
                            View allocation information
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ),
                style: {
                  height: "80px",
                  width: "30vw",
                },
              })}
            />
          </div>
        ) : (
          <div className="mt-8">{renderTable(tableData, null)}</div>
        )}
      </div>
      {openAllocationsDialog && (
        <SupplierAllocationsDialog
          open={openAllocationsDialog}
          onClose={handleAllocationDialogClose}
          allocationsList={allocationsList}
        ></SupplierAllocationsDialog>
      )}
    </Fragment>
  );
}

export default SupplierEmissionSources;
